import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  Typography,
  IconButton,
  Box,
  Chip,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  Button,
  TextField,
  Collapse
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Comment,
  Share,
  LocationOn,
  CalendarToday,
  Star,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';

const PostCard = ({ post, onUpdate }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likeCount || 0);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState(post.comments || []);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && post.likes) {
      setLiked(post.likes.some(like => like.user === user.id));
    }
  }, [user, post.likes]);

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.post(getApiUrl(`/api/posts/${post._id}/like`));
      setLiked(response.data.isLiked);
      setLikeCount(response.data.likeCount);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleComment = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!newComment.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post(getApiUrl(`/api/posts/${post._id}/comment`), {
        text: newComment.trim()
      });
      
      setComments(prev => [...prev, response.data.comment]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      await axios.post(getApiUrl(`/api/posts/${post._id}/share`));
      // You could add a success notification here
    } catch (error) {
      console.error('Error sharing post:', error);
    }
  };

  const getTripTypeColor = (type) => {
    const colors = {
      solo: '#8b5cf6',
      couple: '#ec4899',
      family: '#6366f1',
      friends: '#f59e0b',
      business: '#64748b',
      adventure: '#10b981',
      relaxation: '#06b6d4'
    };
    return colors[type] || '#64748b';
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        sx={{
          color: i < rating ? '#f59e0b' : '#e2e8f0',
          fontSize: '1rem'
        }}
      />
    ));
  };

  return (
    <>
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        {/* Header */}
        <CardHeader
          avatar={
            <Avatar
              src={post.author?.profilePicture}
              alt={post.author?.fullName}
              onClick={() => navigate(`/profile/${post.author?.username}`)}
              sx={{ cursor: 'pointer' }}
            >
              {post.author?.fullName?.charAt(0)}
            </Avatar>
          }
          title={
            <Box>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate(`/profile/${post.author?.username}`)}
              >
                {post.author?.fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                @{post.author?.username}
              </Typography>
            </Box>
          }
          subheader={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LocationOn sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {post.location?.name}
              </Typography>
              <CalendarToday sx={{ fontSize: '1rem', color: 'text.secondary', ml: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {format(new Date(post.tripDate?.startDate), 'MMM dd, yyyy')}
              </Typography>
            </Box>
          }
        />

        {/* Content */}
        <CardContent>
          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/post/${post._id}`)}
          >
            {post.title}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            {post.description}
          </Typography>

          {/* Trip Details */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              label={post.tripType}
              size="small"
              sx={{
                backgroundColor: getTripTypeColor(post.tripType),
                color: 'white',
                textTransform: 'capitalize'
              }}
            />
            <Chip
              label={post.budget}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {renderStars(post.rating)}
            </Box>
          </Box>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {post.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={`#${tag}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Box>
          )}

          {/* Media */}
          {post.media && post.media.length > 0 && (
            <ImageList
              sx={{ width: '100%', maxHeight: 400 }}
              cols={post.media.length === 1 ? 1 : 2}
              rowHeight={200}
            >
              {post.media.slice(0, 4).map((item, index) => (
                <ImageListItem
                  key={index}
                  onClick={() => setSelectedImage(item)}
                  sx={{ cursor: 'pointer' }}
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={`Trip media ${index + 1}`}
                      loading="lazy"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  ) : (
                    <video
                      src={item.url}
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      muted
                    />
                  )}
                  {post.media.length > 4 && index === 3 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      +{post.media.length - 4}
                    </Box>
                  )}
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </CardContent>

        {/* Actions */}
        <CardActions disableSpacing>
          <IconButton onClick={handleLike} color={liked ? 'error' : 'default'}>
            {liked ? <Favorite /> : <FavoriteBorder />}
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {likeCount}
          </Typography>

          <IconButton onClick={() => setShowComments(!showComments)}>
            <Comment />
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {comments.length}
          </Typography>

          <IconButton onClick={handleShare}>
            <Share />
          </IconButton>
          <Typography variant="body2">
            {post.shareCount || 0}
          </Typography>

          <IconButton
            onClick={() => setShowComments(!showComments)}
            sx={{ ml: 'auto' }}
          >
            {showComments ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </CardActions>

        {/* Comments Section */}
        <Collapse in={showComments} timeout="auto" unmountOnExit>
          <CardContent>
            {/* Add Comment */}
            {isAuthenticated && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleComment();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleComment}
                  disabled={!newComment.trim() || loading}
                >
                  Post
                </Button>
              </Box>
            )}

            {/* Comments List */}
            {comments.map((comment, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Avatar
                  src={comment.user?.profilePicture}
                  sx={{ width: 32, height: 32 }}
                >
                  {comment.user?.fullName?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    <strong>{comment.user?.fullName}</strong> {comment.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(comment.createdAt), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Collapse>
      </Card>

      {/* Image Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            selectedImage.type === 'image' ? (
              <img
                src={selectedImage.url}
                alt="Trip media"
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <video
                src={selectedImage.url}
                controls
                style={{ width: '100%', height: 'auto' }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostCard;
